import L from "leaflet";
import { VelocityDatum } from "@/lib/wind";

/**
 * Wind-speed color stops inspired by Windy.com.
 * Each entry: [speed m/s, [r, g, b]]
 */
const COLOR_STOPS: [number, [number, number, number]][] = [
  [0, [98, 113, 183]], // calm — soft blue
  [2, [57, 160, 96]], // light — green
  [4, [130, 196, 64]], // gentle — yellow-green
  [6, [202, 210, 57]], // moderate — yellow
  [8, [252, 207, 45]], // fresh — gold
  [10, [251, 158, 43]], // strong — orange
  [13, [243, 96, 31]], // near gale — red-orange
  [16, [224, 47, 25]], // gale — red
  [20, [178, 24, 57]], // severe — dark red
  [25, [128, 11, 103]], // storm — purple
];

function interpolateColor(speed: number): [number, number, number, number] {
  if (speed <= COLOR_STOPS[0][0]) return [...COLOR_STOPS[0][1], 160];
  if (speed >= COLOR_STOPS[COLOR_STOPS.length - 1][0])
    return [...COLOR_STOPS[COLOR_STOPS.length - 1][1], 160];

  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const [s0, c0] = COLOR_STOPS[i];
    const [s1, c1] = COLOR_STOPS[i + 1];
    if (speed >= s0 && speed < s1) {
      const t = (speed - s0) / (s1 - s0);
      return [
        Math.round(c0[0] + t * (c1[0] - c0[0])),
        Math.round(c0[1] + t * (c1[1] - c0[1])),
        Math.round(c0[2] + t * (c1[2] - c0[2])),
        160, // alpha
      ];
    }
  }
  return [...COLOR_STOPS[0][1], 160];
}

/**
 * Creates a canvas-based image overlay showing wind speed as a color heatmap.
 * Uses bilinear interpolation for smooth gradients between grid points.
 */
export function createWindSpeedOverlay(
  data: VelocityDatum[],
): L.ImageOverlay | null {
  if (data.length < 2) return null;

  const uComponent = data[0];
  const vComponent = data[1];
  const { header } = uComponent;
  const { nx, ny, lo1, la1, dx, dy } = header;

  const lo2 = lo1 + (nx - 1) * dx;
  const la2 = la1 - (ny - 1) * dy;

  // Compute wind speed grid
  const speedGrid: number[][] = [];
  for (let y = 0; y < ny; y++) {
    const row: number[] = [];
    for (let x = 0; x < nx; x++) {
      const idx = y * nx + x;
      const u = uComponent.data[idx] ?? 0;
      const v = vComponent.data[idx] ?? 0;
      row.push(Math.sqrt(u * u + v * v));
    }
    speedGrid.push(row);
  }

  // Render to canvas with higher resolution for smooth gradients
  const scale = 4; // pixels per grid cell
  const canvasWidth = (nx - 1) * scale;
  const canvasHeight = (ny - 1) * scale;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const imageData = ctx.createImageData(canvasWidth, canvasHeight);
  const pixels = imageData.data;

  for (let py = 0; py < canvasHeight; py++) {
    for (let px = 0; px < canvasWidth; px++) {
      // Map pixel to grid position
      const gx = px / scale;
      const gy = py / scale;

      // Bilinear interpolation
      const x0 = Math.floor(gx);
      const y0 = Math.floor(gy);
      const x1 = Math.min(x0 + 1, nx - 1);
      const y1 = Math.min(y0 + 1, ny - 1);
      const fx = gx - x0;
      const fy = gy - y0;

      const s00 = speedGrid[y0][x0];
      const s10 = speedGrid[y0][x1];
      const s01 = speedGrid[y1][x0];
      const s11 = speedGrid[y1][x1];

      const speed =
        s00 * (1 - fx) * (1 - fy) +
        s10 * fx * (1 - fy) +
        s01 * (1 - fx) * fy +
        s11 * fx * fy;

      const [r, g, b, a] = interpolateColor(speed);
      const offset = (py * canvasWidth + px) * 4;
      pixels[offset] = r;
      pixels[offset + 1] = g;
      pixels[offset + 2] = b;
      pixels[offset + 3] = a;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const bounds = L.latLngBounds(
    L.latLng(la2, lo1), // south-west
    L.latLng(la1, lo2), // north-east
  );

  return L.imageOverlay(canvas.toDataURL(), bounds, {
    opacity: 0.65,
    interactive: false,
  });
}
